// =============================================================================
// A time field for the event form. The native picker (an iOS wheel, a
// dropdown on the desktop) moves in 5-minute steps so a time is a short
// scroll, not a hunt through sixty minutes; the keyboard button beside it
// switches to a plain text box for the odd 14:37, then hands the value back.
// =============================================================================

import React, { useEffect, useRef, useState } from 'react';
import { Keyboard, Clock } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';

export const normalizeTime = (raw) => {
  const m = /^\s*(\d{1,2})\s*[:.]?\s*(\d{2})\s*$/.exec(raw || '');
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
};

const TimeField = ({ value, onChange, className = '', ariaLabel }) => {
  const { t } = useTranslation();
  const [typing, setTyping] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => {
    if (typing) {
      setDraft(value || '');
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [typing, value]);

  const commit = () => {
    const next = normalizeTime(draft);
    if (next && next !== value) onChange(next);
    setTyping(false);
  };

  return (
    <div className="flex items-center gap-1 min-w-0">
      {typing ? (
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          placeholder="14:37"
          className={className}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            } else if (e.key === 'Escape') {
              setTyping(false);
            }
          }}
          aria-label={ariaLabel}
        />
      ) : (
        <input
          type="time"
          step="300"
          className={className}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          aria-label={ariaLabel}
        />
      )}
      <button
        type="button"
        onClick={() => setTyping((v) => !v)}
        className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 flex-shrink-0"
        title={typing ? t('calendar.pickTime') : t('calendar.typeTime')}
        aria-label={typing ? t('calendar.pickTime') : t('calendar.typeTime')}
      >
        {typing ? <Clock size={16} /> : <Keyboard size={16} />}
      </button>
    </div>
  );
};

export default TimeField;
